import useDebounce from "src/hooks/useDebounce";
import { FC, Fragment, useEffect, useState } from "react";
import {
    Autocomplete,
    TextField,
    Box,
    Collapse,
    MenuItem,
    Stack,
    Typography,
    Divider,
} from "@mui/material";
import { CardNeutral } from "src/components/card";
import { useTranslation } from "react-i18next";
import LoadingButton from "src/components/LoadingButton";
import { useSearchParams } from "react-router-dom";
import { FilterQueryParams, parseQueryParams } from "src/utils/other";
import { DatePicker } from "@mui/x-date-pickers";
import { format, formatISO } from "date-fns";
import { AutocompleteList } from "src/utils/response";
import AutocompleteListItem from "src/components/AutocompleteListItem";
import { getDateFromToFromPeriod } from "src/utils/date";

export interface FilterFieldItem {
    key: string;
    label?: string;
    type: "text" | "select" | "autocomplete" | "date" | "divider" | "blank";
    options?: { key: string; label?: string }[];
    autocompleteOptions?: AutocompleteList;
    isLoading?: boolean;
    isIdField?: boolean;
    disabled?: boolean;
    isHidden?: { field: string; value: any }[];
    isVisible?: { field: string; value: any }[];
    shouldHideOptions?: boolean;
    clearFieldsOnChange?: string[];
    selectItemCallback?: (itemId: string | number) => void;
    fetchDataCallback?: (value: string) => void;
    handlePaste?: () => void;
}

interface OwnProps {
    fields: FilterFieldItem[];
    isVisible: boolean;
    enableAutoFilter?: boolean;
    handleFilter: (searchParams: { [key: string]: string | string[] }) => void;
    handleClear: () => void;
}

const ListFilters: FC<OwnProps> = ({
    fields,
    isVisible,
    enableAutoFilter,
    handleFilter,
    handleClear,
}) => {
    const { t } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();
    const [autocompleteOpen, setAutocompleteOpen] = useState<string | null>(null);
    const searchString = searchParams.toString();
    const hasDates = !!fields.find((f) => f.key === "from");

    const [localFilters, setLocalFilters] = useState<FilterQueryParams>(
        searchString
            ? {
                  ...parseQueryParams(searchString),
              }
            : hasDates
              ? {
                    from: getDateFromToFromPeriod("this_month").from,
                    to: getDateFromToFromPeriod("this_month").to,
                }
              : {}
    );

    const [autocompleteInputs, setAutocompleteInputs] = useState<{
        [key: string]: string;
    } | null>();
    const debouncedInputs = useDebounce(autocompleteInputs, 400);

    const checkIfHidden = (field: FilterFieldItem) => {
        let shouldHide = false;
        if (field?.isHidden && field?.isHidden?.length > 0) {
            field?.isHidden?.forEach((hidden) => {
                if (localFilters[hidden.field] === hidden.value) {
                    shouldHide = true;
                }
            });
        }
        return shouldHide;
    };

    const handleSubmitFilter = () => {
        handleFilter(localFilters);
        Object.keys(localFilters).forEach((key) => {
            setSearchParams((params) => {
                if (localFilters[key]) {
                    params.set(key, `${localFilters[key]}`);
                } else {
                    params.delete(key);
                }
                return params;
            });
        });
    };

    const handleLocalClear = () => {
        handleClear();
        setAutocompleteInputs(null);
        setLocalFilters(hasDates ? { from: localFilters.from, to: localFilters.to } : {});
        fields.forEach((field) => {
            setSearchParams((params) => {
                if (field.type !== "date") {
                    params.delete(field.key);
                }
                return params;
            });
        });
    };

    // setting search query
    const handleFilterChange = (field: string, value: string) => {
        setLocalFilters((prev) => ({ ...prev, [field]: value }));
    };

    // this should reset autocomplete on clear
    useEffect(() => {
        const newAutocompleteFields: { [key: string]: string } = {};
        fields.forEach((field) => {
            if (field.type === "autocomplete") {
                newAutocompleteFields[field.key] = "";
            }
        });
        if (!autocompleteInputs) {
            setAutocompleteInputs(newAutocompleteFields);
        }
    }, [fields, autocompleteInputs]);

    useEffect(() => {
        const locationId = localFilters["locationId"];
        if (!locationId) {
            const newAutocompleteFields: { [key: string]: string } = {};
            fields.forEach((field) => {
                if (field.type === "autocomplete") {
                    newAutocompleteFields[field.key] = "";
                }
            });
            if (!autocompleteInputs) {
                setAutocompleteInputs(newAutocompleteFields);
            }
            setLocalFilters({ ...localFilters, ["stationId"]: "" } as FilterQueryParams);
        }
    }, [localFilters["locationId"]]);

    useEffect(() => {
        const from = searchParams.get("from");
        const to = searchParams.get("to");

        setLocalFilters((prev) => ({
            ...prev,
            from: from && `${from}` !== localFilters["from"] ? from : prev.from,
            to: to && `${to}` !== localFilters["to"] ? to : prev.to,
        }));

        if (
            enableAutoFilter &&
            (`${from}` !== localFilters["from"] || `${to}` !== localFilters["to"])
        ) {
            handleFilter({
                ...localFilters,
                from: from && `${from}` !== localFilters["from"] ? from : localFilters.from,
                to: to && `${to}` !== localFilters["to"] ? to : localFilters.to,
            });
        }
    }, [searchParams.get("from"), searchParams.get("to")]);

    useEffect(() => {
        if (debouncedInputs) {
            Object.keys(debouncedInputs).forEach((key) => {
                const field = fields.find((f) => f.key === key);
                if (field?.fetchDataCallback && debouncedInputs[key]) {
                    field.fetchDataCallback(debouncedInputs[key]);
                }
            });
        }
    }, [debouncedInputs]);

    return (
        <Collapse in={isVisible}>
            <CardNeutral
                sx={{
                    borderRadius: { xs: 0, sm: 1 },
                    mb: { xs: 2, sm: 4 },
                    mx: { xs: -2, sm: 0 },
                    p: { xs: 2, sm: 3 },
                }}
            >
                <Stack spacing={3}>
                    <Typography variant="section">{t("shared.listFilters.filterBy")}</Typography>
                    <Box
                        sx={{
                            display: "grid",
                            gap: 3,
                            gridTemplateColumns: {
                                xs: "repeat(1, 1fr)",
                                sm: "repeat(2, 1fr)",
                            },
                        }}
                    >
                        {fields?.map((field: FilterFieldItem) => {
                            if (
                                field.type === "autocomplete" &&
                                autocompleteInputs &&
                                !checkIfHidden(field)
                            ) {
                                return (
                                    <Autocomplete
                                        key={field.shouldHideOptions ? "0" : field.key}
                                        multiple
                                        limitTags={1}
                                        disabled={!!field.disabled}
                                        disablePortal
                                        loading={field.isLoading}
                                        inputValue={
                                            field.shouldHideOptions
                                                ? ""
                                                : autocompleteInputs[field.key] || ""
                                        }
                                        open={
                                            !!(autocompleteOpen && autocompleteOpen === field.key)
                                        }
                                        onInputChange={(e, value) => {
                                            setAutocompleteInputs({
                                                ...autocompleteInputs,
                                                [field.key]: value,
                                            });

                                            if (value.length === 0) {
                                                if (autocompleteOpen) setAutocompleteOpen(null);
                                            } else {
                                                if (!autocompleteOpen)
                                                    setAutocompleteOpen(field.key);
                                            }
                                        }}
                                        onChange={(event, value) => {
                                            handleFilterChange(
                                                field.key,
                                                value?.length > 0 ? value?.[0].id : ""
                                            );
                                            if (field.selectItemCallback) {
                                                field.selectItemCallback(
                                                    value?.length > 0 ? value?.[0].id : ""
                                                );
                                            }
                                        }}
                                        isOptionEqualToValue={(option, value) => {
                                            return field.shouldHideOptions
                                                ? false
                                                : option.id === value.id;
                                        }}
                                        getOptionDisabled={(option) => {
                                            const selected = localFilters[field.key];
                                            if (!selected) return false;

                                            return !!(selected !== option.id);
                                        }}
                                        renderOption={(props, option) => {
                                            if (field.shouldHideOptions) {
                                                return [];
                                            }
                                            return (
                                                <li {...(props as any)} key={option.id}>
                                                    <AutocompleteListItem option={option} />
                                                </li>
                                            );
                                        }}
                                        noOptionsText={
                                            autocompleteInputs[field.key] === ""
                                                ? ""
                                                : t("shared.noOptions")
                                        }
                                        loadingText={t("shared.loading")}
                                        getOptionLabel={(option) => option.firstLine || ""}
                                        options={
                                            field.shouldHideOptions
                                                ? []
                                                : field.autocompleteOptions || []
                                        }
                                        blurOnSelect={true}
                                        renderInput={(params) => {
                                            return (
                                                <TextField
                                                    {...params}
                                                    size="small"
                                                    label={field.label || field.key}
                                                    disabled={!!field.disabled}
                                                    InputProps={{
                                                        ...params.InputProps,
                                                        endAdornment: <></>,
                                                    }}
                                                />
                                            );
                                        }}
                                    />
                                );
                            }

                            if (field.type === "date" && !checkIfHidden(field)) {
                                return (
                                    <DatePicker
                                        key={field.key}
                                        label={field.label}
                                        disabled={!!field.disabled}
                                        inputFormat="dd/MM/yyyy"
                                        value={localFilters[field.key] || ""}
                                        onChange={(value) => {
                                            handleFilterChange(
                                                field.key,
                                                value
                                                    ? formatISO(new Date(value as any), {
                                                          representation: "date",
                                                      })
                                                    : ""
                                            );
                                        }}
                                        renderInput={(params) => {
                                            return (
                                                <TextField
                                                    {...params}
                                                    error={undefined}
                                                    inputProps={{
                                                        ...params.inputProps,
                                                        value: localFilters[field.key]
                                                            ? format(
                                                                  new Date(
                                                                      localFilters[
                                                                          field.key
                                                                      ] as string
                                                                  ),
                                                                  "dd/MM/yyyy"
                                                              )
                                                            : params.inputProps?.value || "",
                                                    }}
                                                />
                                            );
                                        }}
                                    />
                                );
                            }

                            if (field.type === "select" && !checkIfHidden(field)) {
                                return (
                                    <TextField
                                        select
                                        key={field.key}
                                        disabled={!!field.disabled}
                                        size="small"
                                        label={field.label || ""}
                                        value={localFilters[field.key] || ""}
                                        onChange={(event) => {
                                            if (
                                                field.clearFieldsOnChange &&
                                                field.clearFieldsOnChange.length > 0
                                            ) {
                                                const convertedFilters = { ...localFilters };
                                                field.clearFieldsOnChange.map((cfon) => {
                                                    delete convertedFilters[cfon];
                                                });

                                                setLocalFilters({
                                                    from: localFilters.from,
                                                    to: localFilters.to,
                                                });
                                                fields.forEach((field) => {
                                                    setSearchParams((params) => {
                                                        if (field.type !== "date") {
                                                            params.delete(field.key);
                                                        }
                                                        return params;
                                                    });
                                                });

                                                setLocalFilters(convertedFilters);
                                                Object.keys(convertedFilters).forEach((key) => {
                                                    setSearchParams((params) => {
                                                        if (localFilters[key]) {
                                                            params.set(key, `${localFilters[key]}`);
                                                        } else {
                                                            params.delete(key);
                                                        }
                                                        return params;
                                                    });
                                                });
                                            }
                                            handleFilterChange(field.key, event.target.value || "");
                                            if (field.selectItemCallback) {
                                                field.selectItemCallback(event.target.value);
                                            }
                                        }}
                                    >
                                        {field.options?.map((o) => (
                                            <MenuItem key={o.key} value={o.key}>
                                                {o.label || o.key}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                );
                            }
                            if (field.type === "divider" && !checkIfHidden(field)) {
                                return (
                                    <Fragment key={field.key}>
                                        <Box>
                                            <Divider
                                                sx={{
                                                    width: { xs: "100%", sm: "calc(200% + 24px)" },
                                                }}
                                            />
                                        </Box>
                                        <Box sx={{ display: { xs: "none", sm: "block" } }} />
                                    </Fragment>
                                );
                            }

                            if (field.type === "blank" && !checkIfHidden(field)) {
                                return (
                                    <Fragment key={field.key}>
                                        <Box sx={{ display: { xs: "none", sm: "block" } }} />
                                    </Fragment>
                                );
                            }

                            if (!checkIfHidden(field)) {
                                return (
                                    <TextField
                                        key={field.key}
                                        name={field.key}
                                        disabled={!!field.disabled}
                                        value={localFilters[field.key] || ""}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                                handleSubmitFilter();
                                            }
                                        }}
                                        onPaste={
                                            !field.isIdField
                                                ? undefined
                                                : () => {
                                                      if (field.handlePaste) {
                                                          field.handlePaste();
                                                          setAutocompleteInputs({});
                                                      }

                                                      fields
                                                          .filter(
                                                              (f) =>
                                                                  f.key !== field.key &&
                                                                  f.type !== "date"
                                                          )
                                                          .forEach((f) => {
                                                              handleFilterChange(f.key, "");
                                                          });
                                                      setAutocompleteInputs(null);
                                                  }
                                        }
                                        onChange={(e) => {
                                            handleFilterChange(field.key, e.target.value);
                                        }}
                                        label={field.label || field.key}
                                    />
                                );
                            }
                        })}
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between", pt: 1 }}>
                        <LoadingButton
                            type="submit"
                            onClick={() => handleSubmitFilter()}
                            variant="contained"
                            size="large"
                        >
                            {t("shared.listFilters.filterBtn")}
                        </LoadingButton>
                        <LoadingButton size="large" onClick={handleLocalClear}>
                            {t("shared.listFilters.clearFilterBtn")}
                        </LoadingButton>
                    </Box>
                </Stack>
            </CardNeutral>
        </Collapse>
    );
};

export default ListFilters;
